import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { RefreshCw, Plus, Link as LinkIcon, Wand2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";

import {
  useListPaymentRemittanceBatches,
  type PaymentRemittanceListRow,
} from "@/lib/api/paymentRemittancePdfs";
import { CommonDestinationTimezone } from "@/lib/utils";
import { RoutePath } from "../routerPaths";

import { CreateBatchDialog } from "./CreateBatchDialog";
import { DownloadLinksDialog } from "./DownloadLinksDialog";
import { PaymentRemittanceSidePanel } from "./side-panel/PaymentRemittanceSidePanel";

/**
 * Barebones payment-remittance-PDFs admin page (v1 testing only).
 *
 * - "Create batch" opens a dialog (customer selection lives inside the
 *   dialog now).
 * - Refresh button refetches the list.
 * - Clicking a row's "Show PDFs" opens a right-hand side panel with
 *   the source PDFs laid out as cards.
 * - Per-row "Download links" button opens a dialog with on-demand
 *   presigned GET URLs for the source PDFs and (when present) the
 *   active assembled PDF.
 */
const PAGE_SIZE = 1000;

export default function PaymentRemittancePdfsPage() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [downloadBatchId, setDownloadBatchId] = useState<string | null>(null);
  const [panelBatchId, setPanelBatchId] = useState<string | null>(null);
  const [pageOffset, setPageOffset] = useState<number>(0);

  const listResult = useListPaymentRemittanceBatches({
    limit: PAGE_SIZE,
    offset: pageOffset,
  });

  const rows = useMemo(
    () => listResult?.data?.data?.rows ?? [],
    [listResult],
  );

  const totalRows = listResult?.data?.data?.total ?? 0;
  const currentPage = Math.floor(pageOffset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

  const columns = useMemo(
    (): DataTableColumn<PaymentRemittanceListRow>[] => [
      {
        id: "actions",
        header: "Actions",
        disableRowClick: true,
        size: 250,
        cell: ({ row }) => (
          <div className="flex flex-row gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                const isOpen = panelBatchId === row.original.id;
                setPanelBatchId(isOpen ? null : row.original.id);
              }}
            >
              {panelBatchId === row.original.id ? "Hide PDFs" : "Show PDFs"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setDownloadBatchId(row.original.id);
              }}
            >
              <LinkIcon className="h-3 w-3 mr-1" />
              Download Links
            </Button>
          </div>
        ),
      },
      {
        id: "customer",
        accessorFn: (row: PaymentRemittanceListRow) => row.Customer.long_name,
        header: "Customer",
        cell: (info) => info.getValue() as string,
        size: 240,
      },
      {
        id: "period_date",
        accessorKey: "period_date",
        header: "Period",
        cell: (info) =>
          moment.utc(info.getValue() as string).format("YYYY-MM"),
        size: 140,
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: (info) => (
          <span className="font-mono text-xs">
            {info.getValue() as string}
          </span>
        ),
        size: 160,
      },
      {
        id: "counts_total_pdfs",
        accessorKey: "counts_total_pdfs",
        header: "PDFs",
        cell: (info) => info.getValue() as number,
        size: 140,
      },
      {
        id: "counts_terminal_pdfs",
        accessorKey: "counts_terminal_pdfs",
        header: "Complete",
        cell: (info) => info.getValue() as number,
        size: 140,
      },
      {
        id: "counts_failed_pdfs",
        accessorKey: "counts_failed_pdfs",
        header: "Failed",
        cell: (info) => info.getValue() as number,
        size: 140,
      },
      {
        id: "counts_total_pages",
        accessorKey: "counts_total_pages",
        header: "Pages",
        cell: (info) => info.getValue() as number,
        size: 140,
      },
      {
        id: "counts_includable_pages",
        accessorKey: "counts_includable_pages",
        header: "Includable Pages",
        cell: (info) => info.getValue() as number,
        size: 160,
      },
      {
        id: "needs_manual_review",
        accessorKey: "needs_manual_review",
        header: "Manual Review",
        cell: (info) => ((info.getValue() as boolean) ? "yes" : "—"),
        size: 160,
      },
      {
        id: "has_active_assembly_version",
        accessorKey: "has_active_assembly_version",
        header: "Assembled?",
        cell: (info) => (info.getValue() ? "yes" : "—"),
        size: 140,
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        cell: (info) =>
          moment
            .tz(info.getValue() as string, CommonDestinationTimezone)
            .format("YYYY-MM-DD HH:mm"),
        size: 170,
      },
    ],
    [panelBatchId],
  );

  const handleRefresh = () => {
    listResult.refetch();
  };

  const handlePrevPage = () => {
    setPageOffset((prev) => Math.max(0, prev - PAGE_SIZE));
  };

  const handleNextPage = () => {
    setPageOffset((prev) => {
      const nextOffset = prev + PAGE_SIZE;
      return nextOffset < totalRows ? nextOffset : prev;
    });
  };

  const isLoading = listResult.isLoading || listResult.isFetching;

  const panelBatch = useMemo(
    () =>
      panelBatchId
        ? rows.find((r: PaymentRemittanceListRow) => r.id === panelBatchId) ?? null
        : null,
    [panelBatchId, rows],
  );

  return (
    // Bounded-height layout so the side panel scrolls within the viewport
    // instead of stretching the whole page. The page is sized to the
    // viewport minus AppPanel chrome; the table+panel row takes the
    // remaining vertical space (`flex-1 min-h-0`) so its children can
    // scroll independently.
    <div className="flex flex-col gap-4 p-4 h-[calc(100vh-6rem)] overflow-hidden">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-extrabold tracking-tight lg:text-3xl">
          Payment Remittance PDFs
        </h1>
        <p className="text-sm text-muted-foreground">
          Barebones v1 testing UI for the new per-PDF orchestration flow.
        </p>
      </div>

      <div className="flex flex-row gap-2 items-end flex-wrap">
        <Button
          variant="outline"
          onClick={() => navigate(RoutePath.PaymentRemittancePdfsNew)}
        >
          <Wand2 className="h-4 w-4 mr-1" />
          Upload & Match
        </Button>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={isLoading}
        >
          <RefreshCw
            className={`h-4 w-4 mr-1 ${isLoading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {totalRows > 0
            ? `Showing ${pageOffset + 1}–${Math.min(pageOffset + PAGE_SIZE, totalRows)} of ${totalRows}`
            : "No results"}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevPage}
            disabled={pageOffset === 0 || isLoading}
          >
            Prev
          </Button>
          <span className="text-sm">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={pageOffset + PAGE_SIZE >= totalRows || isLoading}
          >
            Next
          </Button>
        </div>
      </div>

      <div className="flex flex-row gap-4 flex-1 min-h-0 overflow-hidden">
        <div className="flex-1 min-w-0">
          <DataTable
            stickyHeaders
            enableColumnVisibility
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            virtualizationRowHeight={49}
            virtualizationOverscan={2}
            enableVirtualization
            enableColumnResizing
            data={rows}
            columns={columns}
            defaultSorting={[{ id: "created_at", desc: true }]}
            emptyMessage={isLoading ? "Loading…" : "No batches found"}
          />
        </div>

        {panelBatch && (
          // h-full + min-h-0 + the SidePanelShell's own flex-1
          // overflow-y-auto body lets the panel scroll internally
          // instead of pushing the page taller.
          <div className="w-[400px] shrink-0 h-full min-h-0 border rounded-md bg-card overflow-hidden flex flex-col">
            <PaymentRemittanceSidePanel
              batch={panelBatch}
              onClose={() => setPanelBatchId(null)}
            />
          </div>
        )}
      </div>

      <CreateBatchDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onComplete={() => {
          setCreateOpen(false);
          listResult.refetch();
        }}
      />

      <DownloadLinksDialog
        batchId={downloadBatchId}
        open={downloadBatchId !== null}
        onOpenChange={(open: boolean) => {
          if (!open) setDownloadBatchId(null);
        }}
      />
    </div>
  );
}
